/**
 * Dev-observability logger for the design-load network sequence: create → design-by-uuid.
 * Prints one console.table once the sequence settles (success or terminal failure), showing when
 * each call started (as a timestamp), its offset from the first call, and its total duration.
 */

type DisplayKind = 'create' | 'design-by-uuid';
type HiddenKind = 'create-status'; // observed to keep the debounce alive during polling, never shown as a row
type EndpointKind = DisplayKind | HiddenKind;

interface EndpointMatch {
	test: RegExp;
	kind: EndpointKind;
	label: string;
}

// Order matters: more specific patterns must come before the generic ones they'd otherwise satisfy.
const ENDPOINT_MATCHERS: EndpointMatch[] = [
	{ test: /\/designs\/create-status\//, kind: 'create-status', label: 'Create Status (poll)' },
	{ test: /\/designs\/roof\/design-by-uuid/, kind: 'design-by-uuid', label: 'Design By UUID' },
	{ test: /\/designs\/design-by-uuid/, kind: 'design-by-uuid', label: 'Design By UUID' },
	{ test: /\/designs\/roof\/create/, kind: 'create', label: 'Create Design' },
	{ test: /\/designs(\?|$)/, kind: 'create', label: 'Create Design' },
];

// design-by-uuid can 409 ("not ready") and get retried once the background build finishes — this
// can take up to a few minutes. Also, the first create-status poll doesn't necessarily fire right
// after Create Design responds — measured up to ~15s of client-side delay in practice — so this
// window has to comfortably exceed that gap, not just the 3s poll cadence, or the table flushes
// with just Create Design before the rest of the sequence even starts.
const SETTLE_DEBOUNCE_MS = 30000;

export interface TimingEntry {
	label: string;
	kind: DisplayKind;
	startedAt: Date;
	endedAt?: Date;
	durationMs?: number;
	attempts: number;
	result?: string;
}

let entries: TimingEntry[] = [];
let debounceTimer: ReturnType<typeof setTimeout> | null = null;

function clearDebounce() {
	if (debounceTimer) {
		clearTimeout(debounceTimer);
		debounceTimer = null;
	}
}

function formatTimestamp(date: Date) {
	// e.g. "14:32:07.412" — hour:minute:second (+ ms for precise ordering)
	return `${date.toLocaleTimeString('en-GB', { hour12: false })}.${String(date.getMilliseconds()).padStart(3, '0')}`;
}

function formatDuration(durationMs: number) {
	const minutes = Math.floor(durationMs / 60000);
	const seconds = Math.floor((durationMs % 60000) / 1000);
	const millis = Math.floor(durationMs % 1000);

	if (minutes > 0) return `${minutes}m ${seconds}s ${millis}ms`;
	if (seconds > 0) return `${seconds}s ${millis}ms`;
	return `${millis}ms`;
}

function flush() {
	clearDebounce();
	if (!entries.length) return;
	const firstStart = entries[0].startedAt.getTime();
	const rows: Record<string, string | number>[] = entries.map((entry, index) => ({
		'#': index + 1,
		'API': entry.label,
		'Started At': formatTimestamp(entry.startedAt),
		'Start Offset': index === 0 ? '—' : formatDuration(entry.startedAt.getTime() - firstStart),
		'Total Duration': entry.durationMs !== undefined ? formatDuration(entry.durationMs) : 'never returned',
		'Result': entry.result ?? 'pending',
	}));

	// Grand total = wall-clock from the very first call in this sequence (Create Design if it ran —
	// an already-created design skips straight to Design By UUID, so it may not have) to the last
	// one finishing. When both ran, this equals (Design By UUID start − Create Design start) +
	// Design By UUID's own total duration, i.e. the full span asked for.
	const settledEndTimes = entries.map((e) => e.endedAt?.getTime()).filter((t): t is number => t !== undefined);
	if (settledEndTimes.length) {
		const grandTotalMs = Math.max(...settledEndTimes) - firstStart;
		const spanLabel = entries.length > 1
			? `Total (${entries[0].label} → ${entries[entries.length - 1].label} done)`
			: `Total (${entries[0].label} done)`;
		rows.push({
			'#': '',
			'API': spanLabel,
			'Started At': '—',
			'Start Offset': '—',
			'Total Duration': formatDuration(grandTotalMs),
			'Result': '—',
		});
	}

	// eslint-disable-next-line no-console
	// console.clear();
	// // eslint-disable-next-line no-console
	console.table(rows);
	entries = [];
}

function scheduleFlush(debounceMs: number) {
	clearDebounce();
	debounceTimer = setTimeout(flush, debounceMs);
}

export function startDesignLoadTiming(url: string): TimingEntry | null {
	const match = ENDPOINT_MATCHERS.find((m) => m.test.test(url));
	if (!match) return null;
	clearDebounce(); // fresh activity in the sequence — don't flush out from under it
	if (match.kind === 'create-status') return null; // observed only to keep the debounce alive, never displayed

	// A retried design-by-uuid (after a 409 "not ready") reopens its original entry instead of
	// adding a new row, so "Total Duration" reflects the whole wait, not just the last attempt.
	const existing = entries.find((e) => e.kind === match.kind && e.endedAt !== undefined);
	if (existing) {
		existing.attempts += 1;
		existing.endedAt = undefined;
		existing.durationMs = undefined;
		existing.result = undefined;
		return existing;
	}

	const entry: TimingEntry = { label: match.label, kind: match.kind, startedAt: new Date(), attempts: 1 };
	entries.push(entry);
	return entry;
}

export function endDesignLoadTiming(
	entry: TimingEntry | null,
	opts: { ok: boolean; status?: number; errorCode?: string; responseData?: any },
): void {
	if (!entry) return;
	entry.endedAt = new Date();
	entry.durationMs = entry.endedAt.getTime() - entry.startedAt.getTime();
	const attemptsSuffix = entry.attempts > 1 ? ` (${entry.attempts} attempts)` : '';
	entry.result = (opts.ok
		? 'success'
		: `failed${opts.status ? ` (${opts.status}${opts.errorCode ? ` ${opts.errorCode}` : ''})` : ''}`) + attemptsSuffix;

	const { terminal, debounceMs } = getSettleDecision(entry.kind, opts);
	if (terminal) {
		flush();
	} else {
		scheduleFlush(debounceMs);
	}
}

function getSettleDecision(
	kind: DisplayKind,
	opts: { ok: boolean; status?: number },
): { terminal: boolean; debounceMs: number } {
	if (kind === 'create') {
		// Success leads into design-by-uuid next — don't flush yet. A permanent create failure
		// means design-by-uuid will never be called, so that ends the sequence right here.
		return opts.ok ? { terminal: false, debounceMs: SETTLE_DEBOUNCE_MS } : { terminal: true, debounceMs: 0 };
	}
	// design-by-uuid: a 409 means "not ready yet" — it'll be retried once the build finishes.
	// Anything else (success, or a real failure) is the last of the two APIs settling.
	if (!opts.ok && opts.status === 409) return { terminal: false, debounceMs: SETTLE_DEBOUNCE_MS };
	return { terminal: true, debounceMs: 0 };
}

/* ------------------------------------------------------------------------------------------------
 * Full first-load pipeline timer
 *
 * One row per phase, each stamped twice — when it starts and when it finishes — so its reported
 * duration is its OWN (complete − start), not the elapsed time since the phase before it:
 *
 *   Create Design      start (POST fired)            → complete (create-status READY)
 *   Fetch Roof Design  start (fetchRoofData pending) → complete (fetchRoofData fulfilled)
 *   Shading Analysis   start (run queued/resumed)    → complete (status COMPLETED)
 *
 * A run is "armed" by `markCreateDesignStart` and lives until the terminal mark (or a reset); every
 * other mark is ignored while disarmed, so a design-by-uuid load on its own (reload, direct link,
 * panel change, post-shading refetch) or a later re-bake never prints anything.
 *
 * ---------------------------------------------------------------------------------------------- */

type MilestoneKey = 'create' | 'roofData' | 'shading';

interface Milestone {
	key: MilestoneKey;
	label: string;
	startMs: number; // monotonic ms since the create-design mark, at phase start
	endMs?: number; // …and at phase completion. Duration = endMs − startMs.
	startWallClock: Date; // only for display — never used to compute a duration
	endWallClock?: Date;
}

let pipelineOrigin: number | null = null; // performance.now() at the create-design POST
let milestones: Milestone[] = [];
let latestQueueSnapshot: { waiting: number; active: number } | null = null;
// Background/auto-save mode sends deferShadingAnalysis: false, so shading runs INSIDE create and no
// separate analysis follows the fetch — that run ends (and prints) at the roof-data milestone.
let expectsShadingRun = true;

// Durations come from performance.now(): monotonic, sub-ms, and immune to the system clock being
// adjusted (NTP/DST) mid-run — Date.now() deltas can go backwards over a several-minute span.
const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

/** Open a phase's row. First start wins, so a retried call can't restart a phase already underway. */
function markStart(key: MilestoneKey, label: string) {
	if (pipelineOrigin === null) return; // not armed → ignore
	if (milestones.some((m) => m.key === key)) return; // first occurrence of each phase only
	milestones.push({ key, label, startMs: now() - pipelineOrigin, startWallClock: new Date() });
}

/**
 * Close a phase's row, giving it its own duration. A completion with no recorded start (the shading
 * run that had already finished before the design fetch resolved) opens the row at the previous
 * phase's end, so the pipeline stays gap-free rather than dropping the row.
 */
function markComplete(key: MilestoneKey, label?: string) {
	if (pipelineOrigin === null) return; // not armed → ignore
	const elapsedMs = now() - pipelineOrigin;
	let milestone = milestones.find((m) => m.key === key);
	if (!milestone) {
		const previousEnd = milestones[milestones.length - 1]?.endMs ?? elapsedMs;
		milestone = { key, label: label ?? key, startMs: previousEnd, startWallClock: new Date() };
		milestones.push(milestone);
	}
	if (milestone.endMs !== undefined) return; // first completion of each phase only
	if (label) milestone.label = label;
	milestone.endMs = elapsedMs;
	milestone.endWallClock = new Date();
}

/**
 * Phase 1 start — the create-design POST is about to fire. Starts (or restarts) the measurement.
 * `expectShadingRunInBg: false` (background/auto-save create, where shading is not deferred) makes the
 * roof-data fetch the terminal step, since no post-fetch shading analysis will run.
 */
export function markCreateDesignStart(opts?: { expectShadingRunInBg?: boolean }): void {
	pipelineOrigin = now();
	expectsShadingRun = opts?.expectShadingRunInBg !== false;
	milestones = [{ key: 'create', label: 'Create Design (POST → build READY)', startMs: 0, startWallClock: new Date() }];
}

/** Phase 1 complete — the background build reported READY, so the design exists server-side. */
export function markCreateDesignComplete(): void {
	markComplete('create');
}

/** Phase 2 start — design-by-uuid (fetchRoofData) is in flight. */
export function markFetchRoofDesignStart(): void {
	markStart('roofData', 'Fetch Roof Design (design-by-uuid)');
}

/**
 * Phase 2 complete — design-by-uuid resolved and the design is in the store.
 * Terminal (prints here) when no shading run is expected.
 */
export function markFetchRoofDesignComplete(): void {
	markComplete('roofData', 'Fetch Roof Design (design-by-uuid)');
	if (!expectsShadingRun) flushPipeline();
}

/** Phase 3 start — a shading run is queued/running and being watched. */
export function markShadingStart(opts?: { status?: string }): void {
	// No separate run is coming (auto-save create bakes shading inside the build), so opening a row
	// for one would print it as "never completed" next to the terminal roof-data phase.
	if (!expectsShadingRun) return;
	markStart('shading', `Shading analysis${opts?.status ? ` (from ${opts.status.toLowerCase()})` : ''}`);
}

/**
 * Phase 3 complete (terminal) — the first shading run finished; prints the summary and disarms.
 *
 * `note` is for the case where the run had ALREADY finished by the time the design fetch
 * resolved: there is no start to measure from, so the row says so rather than implying the run
 * took the time it is credited with.
 */
export function markShadingComplete(opts: { status: string; inSync?: boolean; note?: string }): void {
	if (pipelineOrigin === null) return;
	markComplete(
		'shading',
		`Shading analysis (${opts.status.toLowerCase()})`
			+ (opts.inSync === false ? ' (out of sync)' : '')
			+ (opts.note ? ` — ${opts.note}` : ''),
	);
	flushPipeline();
}

/**
 * Terminal for a design that will never have a shading run to wait for — a non-simulation mode, or
 * no shading_analysis row at all. Without this the armed timer sits waiting for a third milestone
 * that cannot come, and `flushPipeline`'s completeness check drops the whole run silently.
 */
export function markNoShadingRunExpected(): void {
	if (pipelineOrigin === null) return;
	expectsShadingRun = false;
	flushPipeline();
}

/** Records one create-status poll's queue depth. No-op unless a create-design POST armed it. */
export function markQueueSnapshot(metadata: { waiting: number; active: number }): void {
	if (pipelineOrigin === null) return; // not armed → ignore
	latestQueueSnapshot = metadata;
}

/** Abandon the current measurement without printing (create failed, shading failed, …). */
export function resetDesignPipelineTiming(): void {
	pipelineOrigin = null;
	milestones = [];
	latestQueueSnapshot = null;
	expectsShadingRun = true;
}

function flushPipeline() {
	if (pipelineOrigin === null) return;
	// Every phase the run was supposed to reach must be present AND completed. A shading completion
	// with no preceding create (e.g. a user "Done" on an already-open design) is dropped silently.
	const required: MilestoneKey[] = expectsShadingRun ? ['create', 'roofData', 'shading'] : ['create', 'roofData'];
	if (!required.every((key) => milestones.some((m) => m.key === key && m.endMs !== undefined))) {
		resetDesignPipelineTiming();
		return;
	}

	// End-to-end = create-design POST → the last phase completion (every required one is present).
	const totalMs = milestones.reduce((latest, m) => Math.max(latest, m.endMs ?? 0), 0);
	const endLabel = expectsShadingRun ? 'first shading analysis complete' : 'roof data fetched';

	// One row per phase. "At" is when it completed, "Took" is now that phase's OWN duration
	// (complete − start) rather than the gap since the step before it, and "Since Create Design" is
	// the elapsed time at its completion. The last row carries the end-to-end total.
	const rows: Record<string, string>[] = milestones.map((m) => ({
		'Step': m.label,
		'At': formatTimestamp(m.endWallClock ?? m.startWallClock),
		'Took': m.endMs !== undefined ? formatDuration(m.endMs - m.startMs) : 'never completed',
		'Since Create Design': m.endMs !== undefined ? formatDuration(m.endMs) : '—',
	}));
	rows.push({
		'Step': `TOTAL (create-design API call → ${endLabel})`,
		'At': '—',
		'Took': formatDuration(totalMs),
		'Since Create Design': formatDuration(totalMs),
	});

	/* eslint-disable no-console */
	console.log(`[Design Pipeline] TOTAL ${formatDuration(totalMs)} — create-design API call → ${endLabel}`);
	console.table(rows);
	if (latestQueueSnapshot) {
		console.log(`[Design Queue] waiting: ${latestQueueSnapshot.waiting}, active: ${latestQueueSnapshot.active}`);
	}
	/* eslint-enable no-console */

	resetDesignPipelineTiming();
}
